/**
 * Инструмент измерения на карте MapLibre: точка / линия / полигон / круг.
 * Стиль оверлея: stroke #334155, width 1.5, opacity 0.75.
 * Esc / повторный клик по активному режиму — выход (onExit).
 */

import type { Map as MapLibreMap, MapMouseEvent, GeoJSONSource } from "maplibre-gl";
import type { FeatureCollection } from "geojson";
import {
  bearingDegrees,
  circleAreaMeters2,
  circleCircumferenceMeters,
  circleLineString,
  formatAreaMeters2,
  formatBearingDegrees,
  formatHudDistanceMeters,
  formatLngLat,
  haversineDistanceMeters,
  pathLengthMeters,
  ringAreaMeters2,
  type LngLat,
} from "@/lib/map-measure-geo";

export type MeasureMode = "point" | "line" | "polygon" | "circle";

export type MeasurePointResult = {
  lng: number;
  lat: number;
  text: string;
};

/** Пиксели относительно контейнера карты (MapLibre project). */
export type MeasureHudCursor = { x: number; y: number };

export type MapLibreMeasureToolOptions = {
  onDistanceChange?: (
    distanceMeters: number | null,
    label: string | null,
    cursor?: MeasureHudCursor | null,
  ) => void;
  onPointMeasured?: (point: MeasurePointResult | null) => void;
  onExit?: () => void;
};

const SOURCE_ID = "maplibre-measure-source";
const LAYER_LINE = "maplibre-measure-line";
const LAYER_FILL = "maplibre-measure-fill";
const LAYER_POINTS = "maplibre-measure-points";

const STROKE = "#334155";
const STROKE_WIDTH = 1.5;
const STROKE_OPACITY = 0.75;
const FILL_OPACITY = 0.15;

const emptyFc = (): FeatureCollection => ({ type: "FeatureCollection", features: [] });

export class MapLibreMeasureTool {
  private map: MapLibreMap | null = null;
  private active = false;
  private mode: MeasureMode = "line";
  private points: LngLat[] = [];
  private circleCenter: LngLat | null = null;
  private cursor: LngLat | null = null;
  private onDistanceChange?: MapLibreMeasureToolOptions["onDistanceChange"];
  private onPointMeasured?: MapLibreMeasureToolOptions["onPointMeasured"];
  private onExit?: MapLibreMeasureToolOptions["onExit"];
  private boundClick: (e: MapMouseEvent) => void;
  private boundMove: (e: MapMouseEvent) => void;
  private boundDblClick: (e: MapMouseEvent) => void;
  private boundKeyDown: (e: KeyboardEvent) => void;
  private layersReady = false;
  private lastCursorPx: MeasureHudCursor | null = null;

  constructor(
    mapOrOptions?: MapLibreMap | MapLibreMeasureToolOptions | null,
    options: MapLibreMeasureToolOptions = {},
  ) {
    const opts =
      mapOrOptions && typeof mapOrOptions === "object" && "getCanvas" in mapOrOptions
        ? options
        : ((mapOrOptions as MapLibreMeasureToolOptions | null | undefined) ?? options);
    if (mapOrOptions && typeof mapOrOptions === "object" && "getCanvas" in mapOrOptions) {
      this.map = mapOrOptions as MapLibreMap;
    }
    this.onDistanceChange = opts.onDistanceChange;
    this.onPointMeasured = opts.onPointMeasured;
    this.onExit = opts.onExit;
    this.boundClick = this.handleClick.bind(this);
    this.boundMove = this.handleMove.bind(this);
    this.boundDblClick = this.handleDblClick.bind(this);
    this.boundKeyDown = this.handleKeyDown.bind(this);
  }

  setCallbacks(options: MapLibreMeasureToolOptions): void {
    if (options.onDistanceChange !== undefined) this.onDistanceChange = options.onDistanceChange;
    if (options.onPointMeasured !== undefined) this.onPointMeasured = options.onPointMeasured;
    if (options.onExit !== undefined) this.onExit = options.onExit;
  }

  attach(map: MapLibreMap): void {
    this.map = map;
    this.ensureLayers();
  }

  detach(): void {
    this.deactivate();
    this.removeLayers();
    this.map = null;
  }

  isActive(): boolean {
    return this.active;
  }

  getMode(): MeasureMode {
    return this.mode;
  }

  activate(mode: MeasureMode = "line"): void {
    if (!this.map) return;
    this.ensureLayers();
    const modeChanged = this.mode !== mode;
    this.mode = mode;
    if (!this.active) {
      this.map.on("click", this.boundClick);
      this.map.on("mousemove", this.boundMove);
      this.map.on("dblclick", this.boundDblClick);
      window.addEventListener("keydown", this.boundKeyDown);
      this.map.getCanvas().style.cursor = "crosshair";
      this.map.doubleClickZoom.disable();
      this.active = true;
      this.clearGeometry();
      return;
    }
    if (modeChanged) {
      this.clearGeometry();
    }
  }

  /** Выход из режима с колбэком onExit. */
  exit(): void {
    if (!this.active) return;
    this.deactivate(false);
    this.onExit?.();
  }

  deactivate(emitNull = true): void {
    if (!this.map || !this.active) {
      this.clearGeometry(emitNull);
      return;
    }
    this.map.off("click", this.boundClick);
    this.map.off("mousemove", this.boundMove);
    this.map.off("dblclick", this.boundDblClick);
    window.removeEventListener("keydown", this.boundKeyDown);
    this.map.getCanvas().style.cursor = "";
    this.map.doubleClickZoom.enable();
    this.active = false;
    this.clearGeometry(emitNull);
  }

  clear(): void {
    this.clearGeometry(true);
  }

  private clearGeometry(emitNull = true): void {
    this.points = [];
    this.circleCenter = null;
    this.cursor = null;
    this.lastCursorPx = null;
    this.render();
    if (emitNull) {
      this.onDistanceChange?.(null, null, null);
      this.onPointMeasured?.(null);
    }
  }

  private emitHud(
    meters: number | null,
    label: string | null,
    cursor?: MeasureHudCursor | null,
  ): void {
    this.onDistanceChange?.(
      meters,
      label,
      cursor === undefined ? this.lastCursorPx : cursor,
    );
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.active) {
      e.preventDefault();
      e.stopPropagation();
      this.exit();
    }
  }

  private handleClick(e: MapMouseEvent): void {
    if (!this.active || !this.map) return;
    const ll: LngLat = [e.lngLat.lng, e.lngLat.lat];

    if (this.map) {
      const p = this.map.project(e.lngLat);
      this.lastCursorPx = { x: p.x, y: p.y };
    }

    if (this.mode === "point") {
      this.points = [ll];
      this.render();
      const text = formatLngLat(ll[0], ll[1]);
      this.onPointMeasured?.({ lng: ll[0], lat: ll[1], text });
      this.emitHud(null, text);
      return;
    }

    if (this.mode === "circle") {
      if (!this.circleCenter) {
        this.circleCenter = ll;
        this.points = [ll];
        this.render();
        this.emitHud(null, "Укажите радиус");
      } else {
        const r = haversineDistanceMeters(this.circleCenter, ll);
        this.points = [this.circleCenter, ll];
        this.render();
        this.emitCircle(r);
      }
      return;
    }

    if (this.mode === "line") {
      this.points.push(ll);
      this.render();
      this.emitLine();
      return;
    }

    // polygon
    this.points.push(ll);
    this.render();
    this.emitPolygon();
  }

  private handleMove(e: MapMouseEvent): void {
    if (!this.active || !this.map) return;
    this.cursor = [e.lngLat.lng, e.lngLat.lat];
    const p = this.map.project(e.lngLat);
    this.lastCursorPx = { x: p.x, y: p.y };
    this.render();
    if (this.mode === "line" && this.points.length >= 1) {
      this.emitLine(true);
    } else if (this.mode === "polygon" && this.points.length >= 1) {
      this.emitPolygon(true);
    } else if (this.mode === "circle" && this.circleCenter) {
      const r = haversineDistanceMeters(this.circleCenter, this.cursor);
      this.emitCircle(r);
    }
  }

  private handleDblClick(e: MapMouseEvent): void {
    if (!this.active) return;
    e.preventDefault();
    if (this.mode === "line" && this.points.length >= 2) {
      this.emitLine(false);
    } else if (this.mode === "polygon" && this.points.length >= 3) {
      this.emitPolygon(false);
    }
  }

  private emitLine(_preview = false): void {
    const fixed = this.points;
    const pts = this.previewPoints();
    if (pts.length < 2) {
      this.emitHud(null, fixed.length === 0 ? "Кликните точки линии" : null);
      return;
    }
    const total = pathLengthMeters(pts);
    const a = pts[pts.length - 2]!;
    const b = pts[pts.length - 1]!;
    const current = haversineDistanceMeters(a, b);
    const az = bearingDegrees(a, b);
    const label = [
      `Общая дистанция: ${formatHudDistanceMeters(total)}`,
      `Текущая: ${formatHudDistanceMeters(current)}`,
      `Азимут: ${formatBearingDegrees(az)}`,
    ].join("\n");
    this.emitHud(total, label);
  }

  private emitPolygon(_preview = false): void {
    const pts = this.previewPoints();
    if (pts.length < 2) {
      this.emitHud(null, "Кликните вершины полигона");
      return;
    }
    const current = haversineDistanceMeters(pts[pts.length - 2], pts[pts.length - 1]);
    if (pts.length < 3) {
      this.emitHud(
        current,
        [`Текущая сторона: ${formatHudDistanceMeters(current)}`, "Добавьте вершины"].join("\n"),
      );
      return;
    }
    const area = ringAreaMeters2(pts);
    const perim = pathLengthMeters([...pts, pts[0]]);
    const label = [
      `Площадь: ${formatAreaMeters2(area)}`,
      `Периметр: ${formatHudDistanceMeters(perim)}`,
      `Текущая сторона: ${formatHudDistanceMeters(current)}`,
    ].join("\n");
    this.emitHud(area, label);
  }

  private emitCircle(radiusMeters: number): void {
    const area = circleAreaMeters2(radiusMeters);
    const circ = circleCircumferenceMeters(radiusMeters);
    const label = [
      `Радиус: ${formatHudDistanceMeters(radiusMeters)}`,
      `Площадь: ${formatAreaMeters2(area)}`,
      `Окружность: ${formatHudDistanceMeters(circ)}`,
    ].join("\n");
    this.emitHud(radiusMeters, label);
  }

  private previewPoints(): LngLat[] {
    if (
      this.cursor &&
      (this.mode === "line" || this.mode === "polygon") &&
      this.points.length >= 1
    ) {
      return [...this.points, this.cursor];
    }
    return [...this.points];
  }

  private render(): void {
    if (!this.map || !this.layersReady) return;
    const features: FeatureCollection["features"] = [];

    if (this.mode === "circle" && this.circleCenter) {
      const end = this.points[1] ?? this.cursor;
      if (end) {
        const r = haversineDistanceMeters(this.circleCenter, end);
        const ring = circleLineString(this.circleCenter, r);
        features.push({
          type: "Feature",
          properties: { kind: "circle" },
          geometry: { type: "Polygon", coordinates: [ring] },
        });
        features.push({
          type: "Feature",
          properties: { kind: "radius" },
          geometry: {
            type: "LineString",
            coordinates: [this.circleCenter, end],
          },
        });
      }
      features.push({
        type: "Feature",
        properties: { kind: "vertex" },
        geometry: { type: "Point", coordinates: this.circleCenter },
      });
      if (end) {
        features.push({
          type: "Feature",
          properties: { kind: "vertex" },
          geometry: { type: "Point", coordinates: end },
        });
      }
    } else if (this.mode === "point" && this.points[0]) {
      features.push({
        type: "Feature",
        properties: { kind: "vertex" },
        geometry: { type: "Point", coordinates: this.points[0] },
      });
    } else {
      const pts = this.previewPoints();
      if (this.mode === "polygon" && pts.length >= 3) {
        const ring = [...pts, pts[0]];
        features.push({
          type: "Feature",
          properties: { kind: "poly" },
          geometry: { type: "Polygon", coordinates: [ring] },
        });
      } else if (pts.length >= 2) {
        features.push({
          type: "Feature",
          properties: { kind: "line" },
          geometry: { type: "LineString", coordinates: pts },
        });
      }
      for (const p of this.points) {
        features.push({
          type: "Feature",
          properties: { kind: "vertex" },
          geometry: { type: "Point", coordinates: p },
        });
      }
    }

    const src = this.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    src?.setData({ type: "FeatureCollection", features });
  }

  private ensureLayers(): void {
    if (!this.map || this.layersReady) return;
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, { type: "geojson", data: emptyFc() });
    }
    if (!this.map.getLayer(LAYER_FILL)) {
      this.map.addLayer({
        id: LAYER_FILL,
        type: "fill",
        source: SOURCE_ID,
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: {
          "fill-color": STROKE,
          "fill-opacity": FILL_OPACITY,
        },
      });
    }
    if (!this.map.getLayer(LAYER_LINE)) {
      this.map.addLayer({
        id: LAYER_LINE,
        type: "line",
        source: SOURCE_ID,
        filter: [
          "any",
          ["==", ["geometry-type"], "LineString"],
          ["==", ["geometry-type"], "Polygon"],
        ],
        paint: {
          "line-color": STROKE,
          "line-width": STROKE_WIDTH,
          "line-opacity": STROKE_OPACITY,
          "line-dasharray": [2, 2],
        },
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
      });
    }
    if (!this.map.getLayer(LAYER_POINTS)) {
      this.map.addLayer({
        id: LAYER_POINTS,
        type: "circle",
        source: SOURCE_ID,
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-radius": 5,
          "circle-color": "#ffffff",
          "circle-stroke-color": STROKE,
          "circle-stroke-width": STROKE_WIDTH,
          "circle-opacity": STROKE_OPACITY,
          "circle-stroke-opacity": STROKE_OPACITY,
        },
      });
    }
    this.layersReady = true;
  }

  private removeLayers(): void {
    if (!this.map) return;
    for (const id of [LAYER_POINTS, LAYER_LINE, LAYER_FILL]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID);
    this.layersReady = false;
  }
}
